import { useContext } from "react";
import PageTitle from "./pageTitle";
import ContactForm from "../cards/contactForm";
import Socials from "../cards/socials";
import Faq from "../cards/faq";
import MailSubscription from "../cards/emailSub";
import GoogleMap from "../cards/googleMap";
import { CompanyInfoContext } from "../../App";
import ImageHolder from "../cards/imageHolder";
import { Helmet } from "react-helmet-async";

import { MapPin, Phone, Mail, Sparkles } from "lucide-react";

const pic = {
  url: "https://res.cloudinary.com/dkcjpdk1c/image/upload/v1751542731/woman-having-online-meeting-work_vu3t4a.jpg",
};

const Contact = () => {
  const companyInfo = useContext(CompanyInfoContext);
  const dataCheckSocials =
    !companyInfo.company_social || companyInfo.company_social.length === 0;
  const dataCheckFaqs =
    !companyInfo.company_faqs || companyInfo.company_faqs.length === 0;

  return (
    <>
      <Helmet>
        <title>Contact Us - Codehub Technologies</title>
      </Helmet>

      <PageTitle title="Contact Us" />

      <section className="cht-py-12 sm:cht-py-16 cht-bg-white !cht-font-body">
        <div className="cht-max-w-7xl cht-mx-auto cht-px-4 sm:cht-px-6 lg:cht-px-8">
          <div className="cht-grid lg:cht-grid-cols-12 cht-gap-10 lg:cht-gap-12 cht-items-center">
            {/* Image Column */}
            <div className="cht-order-2 lg:cht-order-1 lg:cht-col-span-5">
              <ImageHolder image={pic.url} />
            </div>

            {/* Content Column */}
            <div className="cht-order-1 lg:cht-order-2 lg:cht-col-span-7">
              <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-1.5 cht-rounded-full cht-text-xs cht-font-semibold cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-mb-3 cht-tracking-wide cht-uppercase">
                Get In Touch
              </span>
              <h2 className="cht-text-3xl sm:cht-text-4xl cht-font-bold !cht-font-heading cht-text-[#1e293b] cht-tracking-tight">
                Let's Connect and Ignite Possibilities
              </h2>
              <p className="cht-text-base sm:cht-text-lg cht-text-slate-600 cht-leading-relaxed cht-mt-4 cht-mb-8">
                We're just a message away from transforming your vision into
                reality. Whether you have a question, an innovative idea, or
                want to discuss a potential partnership, our engineering and
                consulting team is ready to assist.
              </p>

              {/* Contact Cards Grid */}
              <div className="cht-space-y-4">
                {companyInfo.company_address && (
                  <div className="cht-flex cht-items-start cht-gap-4 cht-p-4 sm:cht-p-5 cht-rounded-[20px] cht-border cht-border-slate-200/80 cht-bg-slate-50/50 hover:cht-bg-white hover:cht-border-[#3e57da]/40 hover:cht-shadow-soft cht-transition-all cht-duration-200">
                    <div className="cht-w-11 cht-h-11 cht-rounded-2xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
                      <MapPin className="cht-w-5 cht-h-5" />
                    </div>
                    <div>
                      <p className="cht-text-xs cht-font-semibold cht-uppercase cht-tracking-wider cht-text-slate-500 cht-mb-0.5">
                        Office Location
                      </p>
                      <p className="cht-text-sm sm:cht-text-base cht-font-medium cht-text-[#1e293b] cht-m-0">
                        {companyInfo.company_address}
                      </p>
                    </div>
                  </div>
                )}

                {companyInfo.telephone && (
                  <a
                    href={`tel:${companyInfo.telephone}`}
                    className="cht-flex cht-items-start cht-gap-4 cht-p-4 sm:cht-p-5 cht-rounded-[20px] cht-border cht-border-slate-200/80 cht-bg-slate-50/50 hover:cht-bg-white hover:cht-border-[#3e57da]/40 hover:cht-shadow-soft cht-transition-all cht-duration-200 !cht-no-underline cht-group"
                  >
                    <div className="cht-w-11 cht-h-11 cht-rounded-2xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] group-hover:cht-bg-[#3e57da] group-hover:cht-text-white cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-transition-colors">
                      <Phone className="cht-w-5 cht-h-5" />
                    </div>
                    <div>
                      <p className="cht-text-xs cht-font-semibold cht-uppercase cht-tracking-wider cht-text-slate-500 cht-mb-0.5">
                        Direct Phone Line
                      </p>
                      <p className="cht-text-sm sm:cht-text-base cht-font-medium cht-text-[#1e293b] group-hover:cht-text-[#3e57da] cht-transition-colors cht-m-0">
                        {companyInfo.telephone}
                      </p>
                    </div>
                  </a>
                )}

                {companyInfo.email && (
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="cht-flex cht-items-start cht-gap-4 cht-p-4 sm:cht-p-5 cht-rounded-[20px] cht-border cht-border-slate-200/80 cht-bg-slate-50/50 hover:cht-bg-white hover:cht-border-[#3e57da]/40 hover:cht-shadow-soft cht-transition-all cht-duration-200 !cht-no-underline cht-group"
                  >
                    <div className="cht-w-11 cht-h-11 cht-rounded-2xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] group-hover:cht-bg-[#3e57da] group-hover:cht-text-white cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-transition-colors">
                      <Mail className="cht-w-5 cht-h-5" />
                    </div>
                    <div>
                      <p className="cht-text-xs cht-font-semibold cht-uppercase cht-tracking-wider cht-text-slate-500 cht-mb-0.5">
                        Email Support
                      </p>
                      <p className="cht-text-sm sm:cht-text-base cht-font-medium cht-text-[#1e293b] group-hover:cht-text-[#3e57da] cht-transition-colors cht-m-0">
                        {companyInfo.email}
                      </p>
                    </div>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {dataCheckSocials ? null : (
        <Socials small={false} socials={companyInfo.company_social} />
      )}

      {dataCheckFaqs ? null : (
        <Faq
          faqList={companyInfo.company_faqs}
          title="Frequently Asked Questions"
        />
      )}

      <MailSubscription />

      <ContactForm />
    </>
  );
};

export default Contact;
